import { useState, useEffect } from "react";
import { ethers } from "ethers";

import useWeb3 from "./useWeb3";

import { RPC_URL, CONTRACT_ADDRESS, ERC721ABI } from "../constants";

async function listTokensOfOwner(account, readProvider): Promise<any[]> {
	if (!account) {
		return [];
	}

	const sentLogs = await readProvider.queryFilter(readProvider.filters.Transfer(account, null));
	const receivedLogs = await readProvider.queryFilter(readProvider.filters.Transfer(null, account));

	const logs = sentLogs.concat(receivedLogs).sort((a, b) => {
		return a.blockNumber - b.blockNumber || a.transactionIndex - b.TransactionIndex;
	});
	const owned = logs.reduce((results, log) => {
		const { from, to, tokenId } = log.args;
		const inbound = to.toLowerCase() === account.toLowerCase();
		const outbound = from.toLowerCase() === account.toLowerCase();

		if (inbound) {
			results.add(tokenId.toString());
		} else if (outbound) {
			results.delete(tokenId.toString());
		}
		return results;
	}, new Set());

	return Array.from(owned);
}

export default function useWeb3Tokens() {
	const [connected, setConnected] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [tokens, setTokens] = useState([]);
	const [loadedFromStorage, setLoadedFromStorage] = useState(false);
	const { account } = useWeb3();

	useEffect(() => {
		if (loaded && connected && loadedFromStorage) {
			localStorage.setItem("nft-tokens", JSON.stringify(tokens));
		}
	}, [tokens, loaded, connected, loadedFromStorage]);

	useEffect(() => {
		if (!loadedFromStorage && !loaded) {
			const saved = JSON.parse(localStorage.getItem("nft-tokens") || "[]");

			setTokens(saved);
			setLoadedFromStorage(true);
		}
	}, [tokens, loadedFromStorage, loaded]);

	useEffect(() => {
		setConnected(Boolean(account));
	}, [account]);

	useEffect(() => {
		const readProvider = new ethers.Contract(CONTRACT_ADDRESS, ERC721ABI.abi, new ethers.providers.JsonRpcProvider(RPC_URL));

		const getUserTokens = async () => {
			const userTokens = await listTokensOfOwner(account, readProvider);

			setTokens(userTokens);
			setLoaded(true);
		};

		if (account && readProvider) {
			getUserTokens();
		}
	}, [account]);

	return { tokens, connected, loaded };
}
